import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Stack } from "@mui/material";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@mui/icons-material/Clear";
import _ from "lodash";
import { userInviteActions } from "_actions";
import useLocationQueryParams from "../../../hooks/useLocationQueryParams";
import useInviteListFetch from "../Utilities/hooks/useInviteListFetch";
import importedStyles from "../../Proactive/Utilities/styles";
import AnchorButton from "../../../common/AnchorButton";
import UserInviteTable from "./UserInviteTable";
import BasicTextField from "../../../common/BasicTextField";
import UserInviteFilterEntry from "../../../common/Filters/UserInviteFilter/UserInviteFilterEntry";

export default function UserInviteList() {
    const dispatch = useDispatch();
    const offsetBase = 10;
    const { params, setParams } = useLocationQueryParams();
    const queryParams = new URLSearchParams(window.location.search);
    const search = queryParams.get("search") || '';
    const inviteStatusRaw = queryParams.get("inviteStatus") || '';
    const organizationRaw = queryParams.get("organization") || '';
    const offset = parseInt(queryParams.get("offset")) || 0;

    const [page, setPage] = useState(() => offset / offsetBase);

    const [searchField, setSearchField] = useState({ inviteSearch: search });

    const [inviteStatus, setInviteStatus] = useState(() =>
        inviteStatusRaw ? inviteStatusRaw.split(',') : []
    );

    const [organization, setOrganization] = useState(() =>
        organizationRaw ? organizationRaw.split(',') : []
    );

    const payload = {
        ...(inviteStatus.length && { inviteStatus: inviteStatus.join(',') }),
        ...(organization.length && { organization: organization.join(',') }),
        ...(search && { search }),
        offset: page * offsetBase,
    };

    const { rows, columns, totalCount, loading } = useInviteListFetch(payload);

    const handlePageChange = (event, nextPage) => {
        setPage(nextPage);

        const queryParam = {
            ...params,
            offset: nextPage * offsetBase,
            ...(inviteStatus.length && { inviteStatus: inviteStatus.join(',') }),
            ...(organization.length && { organization: organization.join(',') }),
            ...(search && { search }),
        };

        setParams(queryParam);
        dispatch(userInviteActions.list(queryParam));
    };

    const handleSearchField = (event) => {
        const { name, value } = event.target;
        setSearchField(prev => ({ ...prev, [name]: value }));
    };

    const handleSearchOnKeyDown = (event) => {
        if (event.keyCode === 13) searchInvites();
    };

    const clearSearchField = () => {
        if (!searchField?.inviteSearch?.trim()) return;

        setSearchField(prev => ({ ...prev, inviteSearch: '' }));
        const { search, ...remainingParams } = params;

        const newUrl = Object.keys(remainingParams).length
            ? `${window.location.pathname}?${new URLSearchParams(remainingParams)}`
            : window.location.pathname;

        window.history.replaceState(null, '', newUrl);
        setParams(remainingParams);
        dispatch(userInviteActions.list(remainingParams));
    };

    const searchInvites = () => {
        if (_.isEmpty(searchField?.inviteSearch)) return;

        const searchParam = { search: searchField.inviteSearch };
        const updatedParams = { ...params, ...searchParam };

        setParams(updatedParams);
        dispatch(userInviteActions.list(updatedParams));
    };

    return (
        <Box sx={styles.root}>
            <Box sx={styles.container}>
                <Stack direction={{ xs: 'column', md: 'row' }} sx={styles.hero} spacing={2}>
                    <Box component={'h1'} sx={importedStyles.headingLarge}>
                        User Invite List
                    </Box>
                    <Box sx={styles.filterContainer}>
                        <Box sx={{ width: '390px' }}>
                            <BasicTextField
                                name={'inviteSearch'}
                                value={searchField?.inviteSearch || ''}
                                startIcon={<SearchIcon sx={styles.icon} />}
                                endIcon={<ClearIcon sx={styles.clearIcon} />}
                                placeholder={'Email or Invited By - press Enter to search'}
                                handleChange={handleSearchField}
                                handleIconAction={clearSearchField}
                                onKeyDown={handleSearchOnKeyDown}
                                sx={{ backgroundColor: '#fff', borderRadius: '10px', height: '40px' }}
                            />
                        </Box>
                        <Box sx={styles.filtersContainer}>
                            <UserInviteFilterEntry />
                        </Box>
                        <AnchorButton label={'Invite user'} link={'/users/create-invite'} />
                    </Box>
                </Stack>

                <Box>
                    <UserInviteTable
                        rows={rows}
                        columns={columns}
                        totalCount={totalCount}
                        page={page}
                        loading={loading}
                        handlePageChange={handlePageChange}
                        clickable={false}
                    />
                </Box>
            </Box>
        </Box>
    );
}

const styles = {
    root: {
        ...importedStyles.root,
        backgroundColor: '#EFEAE7',
        pt: 16,
        pb: 10,
        minHeight: '100vh',
    },
    container: {
        px: { xs: 2, sm: 6 },
        maxWidth: '1440px',
        position: 'relative',
    },
    hero: {
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', md: 'center' },
        mb: { xs: '24px', sm: '40px' },
    },
    filterContainer: {
        width: { xs: '100%', sm: 'auto' },
        height: '40px',
        display: 'flex',
        gap: 2,
    },
    filtersContainer: {
        width: '131px',
        marginRight: '40px',
    },
    icon: {
        color: '#495D5E',
    },
    clearIcon: {
        fontSize: '18px',
        flexShrink: 0,
        color: '#495D5E',
        pointerEvents: 'none',
    },
};